import { type MutableRefObject, useCallback, useEffect, useRef } from "react";

export type DebouncedSaveRequest = {
  /** False once a later save has fired (or the owner unmounted, unless this is the save flushed on unmount), so this one's result is outdated. */
  isLatestSave: () => boolean;
  /** False once a later edit has been scheduled, even if its save has not started yet. */
  isLatestEdit: () => boolean;
};

export type DebouncedSaveOptions<T> = {
  value: T;
  delay: number;
  /** Resolves false when the value was not (or no longer validly) saved, so the snapshot is not recorded. */
  save: (value: T, request: DebouncedSaveRequest) => Promise<boolean | void>;
  /** Enables skip-unchanged: a value serializing to the last saved snapshot is not saved again. */
  serialize?: (value: T) => string;
  /** Last saved snapshot; pass one when other code also reads or seeds it. */
  savedRef?: MutableRefObject<string | null>;
  /** Read when a save is due; true drops it (e.g. a restore is pending, or there is nothing to save yet). */
  shouldSkip?: (value: T) => boolean;
  /** Extra inputs that restart the debounce when they change, besides the value itself; keep its length fixed per caller. */
  rearmOn?: readonly unknown[];
  /** The value at mount: "fire" saves it at once, "debounce" waits like any edit, "skip" ignores it. */
  initial?: "fire" | "debounce" | "skip";
  /** Saves a still-pending value on unmount instead of dropping it; a function is read at unmount time. */
  flushOnUnmount?: boolean | (() => boolean);
  /** Shared promise chain that serializes saves: a save due while another runs is appended, not dropped. */
  queueRef?: MutableRefObject<Promise<unknown>>;
  /** Called when an edit that will be saved is scheduled. */
  onSchedule?: () => void;
};

const NOTHING_PENDING = Symbol("nothing pending");

/** Debounced autosave: the timer, skip-unchanged snapshot, pending flush and latest-wins request checks in one place. */
export const useDebouncedSave = <T>(options: DebouncedSaveOptions<T>): { flush: () => void } => {
  const { value, delay, initial = "fire", rearmOn = [] } = options;
  // Everything except the value and delay is read when the save runs, not when it was scheduled.
  const latestOptionsRef = useRef(options);
  latestOptionsRef.current = options;

  const internalSavedRef = useRef<string | null>(null);
  const pendingValueRef = useRef<T | typeof NOTHING_PENDING>(NOTHING_PENDING);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editVersionRef = useRef(0);
  const saveVersionRef = useRef(0);
  const isMountedRef = useRef(true);
  const isFirstValueRef = useRef(true);
  // A sent snapshot not yet recorded as saved: a superseded save can still reach the server without updating the saved snapshot.
  const unconfirmedSnapshotRef = useRef<string | null>(null);

  const runSave = useCallback((valueToSave: T, editVersion: number, { isUnmountFlush = false } = {}) => {
    const { shouldSkip, queueRef } = latestOptionsRef.current;
    // Every fire supersedes the previous save, even one that is then skipped, as the old autosaves cancelled on every new value.
    saveVersionRef.current += 1;
    const saveVersion = saveVersionRef.current;
    if (shouldSkip?.(valueToSave)) return;
    const request: DebouncedSaveRequest = {
      isLatestSave: () => (isMountedRef.current || isUnmountFlush) && saveVersionRef.current === saveVersion,
      isLatestEdit: () => editVersionRef.current === editVersion,
    };

    const execute = async () => {
      const { save, serialize, savedRef = internalSavedRef } = latestOptionsRef.current;
      try {
        // Checked when the save actually starts: an earlier queued save may already have stored this value.
        const snapshot = serialize?.(valueToSave);
        // A value reverted while an earlier save was out is sent again, since that earlier save may have overwritten it on the server.
        const mayDifferOnServer = unconfirmedSnapshotRef.current !== null && unconfirmedSnapshotRef.current !== snapshot;
        if (snapshot !== undefined && snapshot === savedRef.current && !mayDifferOnServer) return;
        if (snapshot !== undefined) unconfirmedSnapshotRef.current = snapshot;
        const result = await save(valueToSave, request);
        if (result !== false && snapshot !== undefined) {
          savedRef.current = snapshot;
          if (unconfirmedSnapshotRef.current === snapshot) unconfirmedSnapshotRef.current = null;
        }
      } catch {
        // save owns its error reporting; a rejection (or a throwing serializer) must not break the queue.
      }
    };

    if (queueRef) queueRef.current = queueRef.current.then(execute);
    else execute();
  }, []);

  const flushPending = useCallback(
    (isUnmountFlush: boolean) => {
      const pendingValue = pendingValueRef.current;
      if (pendingValue === NOTHING_PENDING) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      pendingValueRef.current = NOTHING_PENDING;
      runSave(pendingValue, editVersionRef.current, { isUnmountFlush });
    },
    [runSave]
  );
  const flush = useCallback(() => flushPending(false), [flushPending]);

  useEffect(
    () => {
      const { shouldSkip, onSchedule } = latestOptionsRef.current;
      const isFirstValue = isFirstValueRef.current;
      isFirstValueRef.current = false;
      if (isFirstValue && initial === "skip") return undefined;

      if (!shouldSkip?.(value)) {
        editVersionRef.current += 1;
        onSchedule?.();
      }
      const editVersion = editVersionRef.current;

      if (isFirstValue && initial === "fire") {
        runSave(value, editVersion);
        return undefined;
      }

      pendingValueRef.current = value;
      const timer = setTimeout(() => {
        timerRef.current = null;
        pendingValueRef.current = NOTHING_PENDING;
        runSave(value, editVersion);
      }, delay);
      timerRef.current = timer;
      return () => clearTimeout(timer);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rearmOn is spread on purpose: the caller's extra restart inputs
    [value, delay, runSave, ...rearmOn]
  );

  useEffect(() => {
    // Set again on mount, since a remount (StrictMode, fast refresh) reuses these refs after the cleanup below.
    isMountedRef.current = true;
    return () => {
      const { flushOnUnmount } = latestOptionsRef.current;
      const shouldFlush = typeof flushOnUnmount === "function" ? flushOnUnmount() : Boolean(flushOnUnmount);
      if (shouldFlush) flushPending(true);
      isMountedRef.current = false;
    };
  }, [flushPending]);

  return { flush };
};
